import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { getApiErrorMessage } from '@/lib/api/error';
import { patchMerchantProduct } from '../service/inventory.service';
import {
  merchantProductDetailQueryOptions,
  merchantProductsQueryKeyPrefix,
} from '../service/query-options';
import type { MerchantProductDetail } from '../service/types';

const productEditFormSchema = z.object({
  category: z.string().max(100),
  isHidden: z.boolean(),
});

type ProductEditFormValues = z.infer<typeof productEditFormSchema>;

interface InventoryProductEditCardProps {
  categoryOptions: string[];
  detail: MerchantProductDetail;
  isOwner: boolean;
  productId: string;
}

function getDefaultValues(detail: MerchantProductDetail): ProductEditFormValues {
  return {
    category: detail.category ?? '',
    isHidden: detail.is_hidden,
  };
}

export function InventoryProductEditCard({
  categoryOptions,
  detail,
  isOwner,
  productId,
}: InventoryProductEditCardProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const form = useForm<ProductEditFormValues>({
    resolver: zodResolver(productEditFormSchema),
    defaultValues: getDefaultValues(detail),
  });

  useEffect(() => {
    form.reset(getDefaultValues(detail));
  }, [detail, form]);

  const categoryValue = form.watch('category');
  const isHiddenValue = form.watch('isHidden');
  const normalizedCategory = categoryValue.trim() || null;
  const hasChanges = normalizedCategory !== detail.category || isHiddenValue !== detail.is_hidden;

  const saveMutation = useMutation({
    mutationFn: async (values: ProductEditFormValues) => {
      return patchMerchantProduct(productId, {
        category: values.category.trim() || null,
        is_hidden: values.isHidden,
      });
    },
    onSuccess: async (updatedProduct) => {
      form.reset({
        category: updatedProduct.category ?? '',
        isHidden: updatedProduct.is_hidden,
      });
      toast.success(t('inventory.edit.updated'));
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: merchantProductsQueryKeyPrefix }),
        queryClient.invalidateQueries({
          queryKey: merchantProductDetailQueryOptions(productId).queryKey,
        }),
      ]);
    },
  });

  const canSubmit = isOwner && hasChanges && !saveMutation.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('inventory.edit.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-1.5">
          <Label htmlFor="category">{t('inventory.edit.category')}</Label>
          <Controller
            control={form.control}
            name="category"
            render={({ field }) => (
              <Select
                value={field.value || '__none__'}
                onValueChange={(nextCategory) => {
                  field.onChange(nextCategory === '__none__' ? '' : nextCategory);
                }}
                disabled={!isOwner || saveMutation.isPending}
              >
                <SelectTrigger id="category" className="w-full">
                  <SelectValue placeholder={t('inventory.edit.select_category')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">{t('inventory.edit.uncategorized')}</SelectItem>
                  {categoryOptions.map((category) => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className="flex items-center justify-between rounded-md border p-3">
          <div className="space-y-0.5">
            <p className="text-sm font-medium">{t('inventory.edit.hidden')}</p>
            <p className="text-muted-foreground text-xs">{t('inventory.edit.hidden_help')}</p>
          </div>
          <Controller
            control={form.control}
            name="isHidden"
            render={({ field }) => (
              <Switch
                id="is_hidden"
                checked={field.value}
                disabled={!isOwner || saveMutation.isPending}
                onCheckedChange={field.onChange}
              />
            )}
          />
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            variant="outline"
            disabled={!isOwner || !hasChanges || saveMutation.isPending}
            onClick={() => form.reset(getDefaultValues(detail))}
          >
            {t('inventory.edit.reset')}
          </Button>
          <Button
            disabled={!canSubmit}
            onClick={form.handleSubmit((values) => saveMutation.mutate(values))}
          >
            {saveMutation.isPending ? t('inventory.edit.saving') : t('inventory.edit.save_changes')}
          </Button>
        </div>

        {saveMutation.isError ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>
              {getApiErrorMessage(saveMutation.error, t('inventory.edit.save_error'))}
            </AlertDescription>
          </Alert>
        ) : null}
      </CardContent>
    </Card>
  );
}
